(() => {
  'use strict';

  class SOPacksPremium {
    constructor(element) {
      if (!element || element.dataset.soPacksInitialized === 'true') return;

      this.element = element;
      this.track = element.querySelector('[data-so-packs-track]');
      this.cards = Array.from(element.querySelectorAll('[data-so-packs-card]'));
      this.dots = Array.from(element.querySelectorAll('.so-packs-dot'));
      this.observer = null;
      this.scrollFrame = null;
      this.onScroll = this.onScroll.bind(this);
      this.element.dataset.soPacksInitialized = 'true';

      this.initAnimations();
      this.initCarousel();
    }

    initAnimations() {
      const items = Array.from(this.element.querySelectorAll('.so-packs-reveal'));
      if (!this.element.classList.contains('so-packs-animate') || !items.length ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
          !('IntersectionObserver' in window)) {
        items.forEach((item) => item.classList.add('is-visible'));
        return;
      }

      items.forEach((item, index) => {
        item.style.transitionDelay = `${Math.min(index * 80, 400)}ms`;
      });

      this.observer = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -30px' });

      items.forEach((item) => this.observer.observe(item));
    }

    initCarousel() {
      if (!this.track || !this.dots.length) return;

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const card = this.cards[Number(dot.dataset.index)];
          if (!card) return;
          this.track.scrollTo({
            left: card.offsetLeft - this.track.offsetLeft,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        });
      });
    }

    onScroll() {
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        if (!this.track || !this.cards.length) return;
        const trackLeft = this.track.getBoundingClientRect().left;
        let closestIndex = 0;
        let closestDistance = Infinity;

        this.cards.forEach((card, index) => {
          const distance = Math.abs(card.getBoundingClientRect().left - trackLeft);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        });

        this.dots.forEach((dot, index) => {
          const active = index === closestIndex;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', String(active));
        });
      });
    }

    selectBlock(blockId) {
      const card = this.element.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`);
      if (card && this.track) {
        card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    destroy() {
      if (this.observer) this.observer.disconnect();
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      delete this.element.dataset.soPacksInitialized;
      delete this.element.soPacksInstance;
    }
  }

  const initialize = (scope = document) => {
    const sections = scope.matches && scope.matches('[data-so-packs]')
      ? [scope]
      : Array.from(scope.querySelectorAll('[data-so-packs]'));

    sections.forEach((section) => {
      if (section.soPacksInstance) return;
      section.soPacksInstance = new SOPacksPremium(section);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-so-packs]') || event.target;
    if (section.soPacksInstance) section.soPacksInstance.destroy();
  });
  document.addEventListener('shopify:section:select', (event) => initialize(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('[data-so-packs]');
    if (section && section.soPacksInstance) section.soPacksInstance.selectBlock(event.detail.blockId);
  });
})();